"use client";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import destr from "destr";
import { AudioLines, PlayCircle } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { ofetch } from "ofetch";
import { useEffect, useRef, useState } from "react";
import sanitizeHtml from "sanitize-html";
import { ErrorElement } from "@/components/ErrorElement";
import { LoadingElement } from "@/components/LoadingElement";
import { Skeleton } from "@/components/ui/skeleton";
import { env } from "@/env";
import { KeywordChart } from "@/lib/podcast-insights/Charts";
import { highlightMatch } from "@/lib/podcast-insights/utils";
import { cn } from "@/lib/utils";

export const KeywordItem = ({
  keyword,
  podcastId,
  ...props
}: React.HTMLProps<HTMLDivElement> & {
  keyword: string;
  podcastId?: string;
}) => {
  const t = useTranslations();
  const {
    data: segmentResults,
    isFetching: segmentsFetching,
    isError: segmentsError,
  } = useReactQuery({
    queryKey: ["podcast-segments", keyword],
    enabled: keyword.length > 1,
    queryFn: async () => {
      const response = ofetch<{
        searchResults: SegmentSearchResult[];
        allPublishedAt: SegmentSearchAggregateData;
      }>(`${env.NEXT_PUBLIC_API_URL}/handleSearchKeywordsInSegments`, {
        parseResponse: destr,
        query: {
          query: keyword,
        },
      });
      return response;
    },
  });

  if (segmentsFetching) {
    return <LoadingElement />;
  } else if (segmentsError || !segmentResults) {
    return <ErrorElement />;
  }

  const hasHits = segmentResults.allPublishedAt.length > 0;

  return (
    <div {...props}>
      <h2 className="space-x-4">
        <span className="font-medium text-xl">{keyword}</span>
        <span className="text-sm">
          {t("search.numberOfHits", {
            hits: segmentResults.allPublishedAt.length,
          })}
        </span>
      </h2>
      <div className="space-y-8">
        {(segmentResults.searchResults.length === 0 && <div>No hits</div>) || (
          <div>
            <SearchSegmentList segments={segmentResults.searchResults} query={keyword} />
          </div>
        )}
      </div>
      {hasHits && (
        <KeywordChart aggregateData={segmentResults.allPublishedAt} className="h-[320] w-full" />
      )}
    </div>
  );
};

export const ResultItemSoundComponent = ({
  searchResult,
}: {
  searchResult: SegmentSearchResult;
}) => {
  const t = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audioElement = audioRef.current;

    if (isExpanded && audioElement) {
      // Listen for the 'loadedmetadata' event
      const handleMetadataLoaded = () => {
        // Seek to the start_ms (converted to seconds)
        audioElement.currentTime = searchResult.start_ms / 1000;
        audioElement.play();
        setIsLoadingAudio(false);
      };

      audioElement.addEventListener("loadedmetadata", handleMetadataLoaded);

      return () => {
        audioElement.removeEventListener("loadedmetadata", handleMetadataLoaded);
      };
    }
  }, [isExpanded, searchResult.start_ms]); // Ensure this re-runs if isExpanded or start_ms changes

  return (
    <div
      className={cn({
        "animate-pulse": isExpanded && isLoadingAudio,
      })}
    >
      {isExpanded && (
        <audio
          preload="metadata"
          controls
          ref={audioRef}
          className={cn({
            hidden: isLoadingAudio,
            "block max-w-full": !isLoadingAudio,
          })}
        >
          <source
            src={`https://pub-f500195f46024a54be5d2ea7a92cf46f.r2.dev/transcription_jobs/${searchResult.job_id}/audio.m4a`}
            type="audio/mpeg"
          />
        </audio>
      )}
      <div
        className={cn({
          "flex justify-between items-center w-full h-12 text-foreground/40 rounded-full px-4 bg-muted":
            !isExpanded || isLoadingAudio,
          hidden: !isLoadingAudio && isExpanded,
        })}
      >
        <button
          title={t("podcastInsights.playAudioButton")}
          onClick={() => {
            setIsExpanded(true);
            setIsLoadingAudio(true);
          }}
          disabled={isExpanded}
        >
          <PlayCircle className="text-primary" size={36} />
        </button>
        <div className="flex space-x-2 items-center">
          <span className={cn({ hidden: !isLoadingAudio || !isExpanded })}>
            {t("general.loadingLabel")}
          </span>
          <AudioLines size={32} />
        </div>
      </div>
    </div>
  );
};

export const SearchSegmentList = ({
  segments,
  query,
}: {
  segments: SegmentSearchResult[];
  query?: string;
}) => {
  return (
    <div className="grid grid-cols-3 gap-4">
      {segments.map((segment) => {
        return <SearchSegmentsItem key={segment.segment_id} segment={segment} query={query} />;
      })}
    </div>
  );
};

const SearchSegmentsItem = ({
  segment,
  query,
}: {
  segment: SegmentSearchResult;
  query?: string;
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const excerptHtml = sanitizeHtml(
    query
      ? highlightMatch({
          text: segment.segment_text,
          query,
        })
      : segment.segment_text,
    {
      allowedTags: ["b"],
    }
  );

  let dateToShow = new Date(segment.published_at);
  if (dateToShow.getTime() === 0) {
    dateToShow = new Date(segment.episode_created_at);
  }

  return (
    <div className="border p-4 flex flex-col justify-between rounded-lg hover:bg-foreground/10 transition-colors duration-200 ease-in-out">
      <div>
        <div className="flex justify-between">
          <p className="uppercase text-sm">{segment.podcast_title}</p>
          <p className="uppercase text-sm">{dateToShow.toLocaleDateString(locale)}</p>
        </div>
        <h3 className="scroll-m-20">{segment.episode_title}</h3>
        <p className="uppercase text-sm mt-2">{t("podcasts.excerpt")}</p>
        <p className="text-sm" dangerouslySetInnerHTML={{ __html: excerptHtml }}></p>
      </div>
      <div className="mt-4">
        <ResultItemSoundComponent searchResult={segment} />
      </div>
    </div>
  );
};
